import React, { useMemo } from "react";
import ChartCanvas from "./ChartCanvas";
import ChartFrame from "./ChartFrame";
import styles from "./Chart.module.css";
import { chartColors, mergeOptions, seriesColor } from "./chartTheme";

/**
 * Doughnut / pie chart with an optional centre readout and side legend.
 *
 *   segments = [{ label: "Core", value: 7, color: "#3b82f6",
 *                 caption: "7 schools", captionSub: "(₹90,737)" }]
 *
 * `color` is optional — slices fall back to the categorical palette.
 * Pass `type="pie"` for a solid pie, or `options` to override anything.
 */
export default function DonutChart({
  /* frame */
  title,
  subtitle,
  actions,
  loading = false,
  error = null,
  emptyText = "No data available.",
  /* data */
  segments = [],
  centerValue,
  centerLabel,
  /* appearance */
  type = "doughnut",
  cutout = "72%",
  showLegend = true,
  showTooltip = true,
  formatValue,
  options: optionsOverride,
  ariaLabel,
}) {
  const slices = useMemo(
    () =>
      segments.map((segment, index) => ({
        ...segment,
        color: segment.color || seriesColor(index),
      })),
    [segments],
  );

  const chartData = useMemo(
    () => ({
      labels: slices.map((slice) => slice.label),
      datasets: [
        {
          data: slices.map((slice) => slice.value ?? 0),
          backgroundColor: slices.map((slice) => slice.color),
          borderWidth: 3,
          borderColor: chartColors().onDark,
          hoverOffset: 4,
        },
      ],
    }),
    [slices],
  );

  const chartOptions = useMemo(() => {
    const color = chartColors();
    const base = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: type === "doughnut" ? cutout : 0,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: showTooltip,
          backgroundColor: color.tooltipBg,
          padding: 12,
          cornerRadius: 8,
          callbacks: formatValue
            ? {
              label: (ctx) => ` ${ctx.label}: ${formatValue(ctx.parsed, ctx.dataIndex)}`,
            }
            : undefined,
        },
      },
    };
    return mergeOptions(base, optionsOverride);
  }, [type, cutout, showTooltip, formatValue, optionsOverride]);

  const isEmpty = !slices.some((slice) => (slice.value ?? 0) > 0);

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      actions={actions}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyText={emptyText}
      bodyClassName={styles.donutBody}
    >
      <div className={styles.donutWrap}>
        <ChartCanvas
          type={type}
          data={chartData}
          options={chartOptions}
          ariaLabel={ariaLabel || title}
        />
        {(centerValue != null || centerLabel) && (
          <div className={styles.donutCenter}>
            <div className={styles.donutCenterVal}>{centerValue}</div>
            {centerLabel && <div className={styles.donutCenterSub}>{centerLabel}</div>}
          </div>
        )}
      </div>

      {showLegend && (
        <div className={styles.donutLegend}>
          {slices.map((slice) => (
            <div key={slice.label} className={styles.donutLegendRow}>
              <span className={styles.donutLegendLeft}>
                <span className={styles.donutDot} style={{ background: slice.color }} />
                {slice.label}
              </span>
              <span className={styles.donutRight}>
                {slice.caption ?? slice.value}
                {slice.captionSub && (
                  <div className={styles.donutRightSub}>{slice.captionSub}</div>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </ChartFrame>
  );
}
